import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Plus, Edit, Trash2, Save, X, Loader2 } from 'lucide-react';
import { fetchBlogs, createBlog, updateBlog, deleteBlog, resolveImageUrl, formatApiError } from '../../services/api';
import ImageUploader from './ImageUploader';

const BLOG_CATEGORIES = ['Tarot Basics', 'Angel Cards', 'Crystal Healing', 'Spiritual Practice', 'Love & Relationships', 'Career Guidance'];

const emptyPost = { title: '', excerpt: '', category: BLOG_CATEGORIES[0], image: '', readTime: '5 min read', content: '', date: '' };

const BlogManager = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyPost);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const data = await fetchBlogs();
      setPosts(data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const startCreate = () => {
    setEditing('new');
    setForm(emptyPost);
    setError('');
  };

  const startEdit = (post) => {
    setEditing(post.id);
    setForm({ ...post });
    setError('');
  };

  const cancel = () => {
    setEditing(null);
    setForm(emptyPost);
    setError('');
  };

  const save = async () => {
    if (!form.title || !form.excerpt || !form.content || !form.image) {
      setError('Title, Excerpt, Content, and Image are required');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const payload = {
        title: form.title,
        excerpt: form.excerpt,
        category: form.category,
        image: form.image,
        readTime: form.readTime || '5 min read',
        content: form.content,
        date: form.date || undefined,
      };
      if (editing === 'new') {
        await createBlog(payload);
      } else {
        await updateBlog(editing, payload);
      }
      await load();
      cancel();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this blog post? This cannot be undone.')) return;
    try {
      await deleteBlog(id);
      await load();
    } catch (err) {
      alert(formatApiError(err));
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-3xl font-bold text-amber-300" style={{ fontFamily: 'Cinzel, serif' }}>Blog Posts</h2>
          <p className="text-amber-100/60 text-sm mt-1">{posts.length} post{posts.length !== 1 ? 's' : ''}</p>
        </div>
        {!editing && (
          <Button onClick={startCreate} className="btn-gold" data-testid="new-blog-btn">
            <Plus className="w-4 h-4 mr-2" />
            New Post
          </Button>
        )}
      </div>

      {editing && (
        <div className="mb-8 p-6 bg-emerald-950/40 border border-amber-600/30 rounded-lg space-y-4">
          <h3 className="text-xl font-semibold text-amber-300" style={{ fontFamily: 'Cinzel, serif' }}>
            {editing === 'new' ? 'Create New Blog Post' : 'Edit Blog Post'}
          </h3>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label className="text-amber-300 text-xs uppercase tracking-wider">Title *</Label>
              <Input
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="Article title"
                className="bg-emerald-950/60 border-amber-600/30 text-amber-100"
                data-testid="blog-title-input"
              />
            </div>
            <div>
              <Label className="text-amber-300 text-xs uppercase tracking-wider">Category *</Label>
              <select
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                className="w-full px-3 py-2 rounded-md bg-emerald-950/60 border border-amber-600/30 text-amber-100"
                data-testid="blog-category-select"
              >
                {BLOG_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-amber-300 text-xs uppercase tracking-wider">Read Time</Label>
              <Input
                value={form.readTime}
                onChange={(e) => setForm({ ...form, readTime: e.target.value })}
                placeholder="5 min read"
                className="bg-emerald-950/60 border-amber-600/30 text-amber-100"
              />
            </div>
            <div>
              <Label className="text-amber-300 text-xs uppercase tracking-wider">Date (YYYY-MM-DD)</Label>
              <Input
                value={form.date}
                onChange={(e) => setForm({ ...form, date: e.target.value })}
                placeholder="2025-12-01 (leave blank for today)"
                className="bg-emerald-950/60 border-amber-600/30 text-amber-100"
              />
            </div>
          </div>

          <div>
            <Label className="text-amber-300 text-xs uppercase tracking-wider">Excerpt (short description) *</Label>
            <Textarea
              value={form.excerpt}
              onChange={(e) => setForm({ ...form, excerpt: e.target.value })}
              placeholder="A brief 1-2 sentence summary..."
              rows={2}
              className="bg-emerald-950/60 border-amber-600/30 text-amber-100"
              data-testid="blog-excerpt-input"
            />
          </div>

          <ImageUploader
            value={form.image}
            onChange={(url) => setForm({ ...form, image: url })}
            label="Featured Image *"
          />

          <div>
            <Label className="text-amber-300 text-xs uppercase tracking-wider">Content *</Label>
            <Textarea
              value={form.content}
              onChange={(e) => setForm({ ...form, content: e.target.value })}
              placeholder="Write your blog post here... Use ## for headings, ### for sub-headings, **bold** for emphasis, - for bullet lists, 1. 2. 3. for numbered lists"
              rows={14}
              className="bg-emerald-950/60 border-amber-600/30 text-amber-100 font-serif-mystical"
              data-testid="blog-content-input"
            />
            <p className="text-xs text-amber-100/50 mt-1">
              Formatting: <code className="text-amber-300">## Heading</code>, <code className="text-amber-300">**bold**</code>, <code className="text-amber-300">- bullet</code>, <code className="text-amber-300">1. number</code>
            </p>
          </div>

          {error && <div className="p-3 rounded bg-red-950/60 border border-red-500/40 text-red-300 text-sm">{error}</div>}

          <div className="flex gap-3 pt-2">
            <Button onClick={save} disabled={saving} className="btn-gold" data-testid="blog-save-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              {saving ? 'Saving...' : 'Save Post'}
            </Button>
            <Button onClick={cancel} className="btn-mystical" data-testid="blog-cancel-btn">
              <X className="w-4 h-4 mr-2" />
              Cancel
            </Button>
          </div>
        </div>
      )}

      {loading ? (
        <p className="text-amber-100/60 text-center py-12">Loading blog posts...</p>
      ) : posts.length === 0 ? (
        <p className="text-amber-100/60 text-center py-12">No blog posts yet. Click "New Post" to create one.</p>
      ) : (
        <div className="space-y-3">
          {posts.map((post) => (
            <div
              key={post.id}
              className="flex items-center gap-4 p-4 bg-emerald-950/30 border border-amber-600/20 rounded-lg hover:border-amber-500/50 transition"
              data-testid={`blog-row-${post.id}`}
            >
              <img
                src={resolveImageUrl(post.image)}
                alt={post.title}
                className="w-20 h-20 object-cover rounded border border-amber-600/30 flex-shrink-0"
              />
              <div className="flex-1 min-w-0">
                <h3 className="text-amber-200 font-semibold truncate" style={{ fontFamily: 'Cinzel, serif' }}>{post.title}</h3>
                <div className="flex items-center gap-2 mt-1">
                  <Badge className="bg-emerald-800/80 text-amber-300 border border-amber-500/30 text-xs">{post.category}</Badge>
                  <span className="text-xs text-amber-100/50">{post.date}</span>
                </div>
                <p className="text-sm text-amber-100/70 line-clamp-1 mt-1">{post.excerpt}</p>
              </div>
              <div className="flex gap-2 flex-shrink-0">
                <Button size="sm" onClick={() => startEdit(post)} className="btn-mystical" data-testid={`edit-blog-${post.id}`}>
                  <Edit className="w-4 h-4" />
                </Button>
                <Button size="sm" onClick={() => handleDelete(post.id)} className="bg-red-900/60 text-red-300 hover:bg-red-900 border border-red-500/30" data-testid={`delete-blog-${post.id}`}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default BlogManager;
